import { parseISO } from 'date-fns';
import { useTranslation } from 'react-i18next';
import rowStyles from '../../SchedulerPage/components/RowLabels/RowLabels.module.css';
import ganttHeader from '../../SchedulerPage/components/TimelineHeader/TimelineHeader.module.css';
import { localizedDayNumber, localizedWeekday, localizedWeekLabel, type CalendarLang } from '../../SchedulerPage/lib/japaneseCalendar';
import { formatDateRange } from '../model/formatDateRange';
import type { IPlanWindow, RestTone } from './workPlanModel';
import styles from './workPlanHeader.module.css';

export interface IWorkPlanHeaderProps {
  window: IPlanWindow;
  /** The 4-week window: the Gantt's week-zoom rows — no weekday names. */
  compact: boolean;
  /** Off when grouped by profession — the rail drops that column. */
  withProfession: boolean;
}

/** A rest day's header colouring — the Gantt's own classes. */
const REST_CLASS: Record<RestTone, string> = {
  saturday: ganttHeader.cellSaturday as string,
  sunday: ganttHeader.cellSunday as string,
  rest: ganttHeader.cellRest as string,
};

/**
 * The sticky header row, in the Gantt's own classes: the rail's column titles,
 * and over the days the month band (today's month highlighted), the ISO week,
 * the weekday (1- and 2-week windows) and the day number, rest days coloured
 * Saturday blue and Sunday red.
 */
export function WorkPlanHeader({ window, compact, withProfession }: IWorkPlanHeaderProps) {
  const { t, i18n } = useTranslation();
  const lang: CalendarLang = i18n.language === 'ja' ? 'ja' : 'en';
  const monthLabel = new Intl.DateTimeFormat(i18n.language, { year: 'numeric', month: 'short' });
  const dayRow = compact ? 3 : 4;
  const todayCol = window.days.findIndex((day) => day.isToday) + 1;
  const restClass = (rest: RestTone | null) => (rest ? REST_CLASS[rest] : '');
  return (
    <>
      <div className={styles.railHead}>
        <div className={`${rowStyles.headerGrid} ${styles.titles}`}>
          <span className={rowStyles.colTask}>{t('activities.workPlan.rail.header')}</span>
          {withProfession && <span className={rowStyles.colMeta}>{t('scheduler.columns.profession')}</span>}
          {/* The squeezed rail's meta columns (see WorkPlanGrid): PROGRESS at its
              floor takes the short "%" rather than a truncated word; the status
              column is a circle wide, so no title. */}
          <span className={`${rowStyles.colMeta} ${rowStyles.colPpc}`}>{t('activities.workPlan.rail.progress')}</span>
          <span className={rowStyles.colMeta} />
        </div>
      </div>
      <div className={styles.timeHead}>
        {/* Today's column, under the week, weekday and day cells. */}
        {todayCol > 0 && <div className={styles.today} style={{ gridRow: `2 / ${dayRow + 1}`, gridColumn: todayCol }} aria-hidden />}
        {window.months.map((band) => (
          <div
            key={band.month}
            className={`${ganttHeader.period} ${band.current ? ganttHeader.periodCurrent : ''}`}
            style={{ gridRow: 1, gridColumn: `${band.col} / span ${band.span}` }}
          >
            {monthLabel.format(parseISO(band.month))}
          </div>
        ))}
        {window.weeks.map((band, w) => (
          <div
            key={band.monday}
            className={`${ganttHeader.dayCell} ${ganttHeader.weekNumberCell}`}
            style={{ gridRow: 2, gridColumn: `${w * 7 + 1} / span 7` }}
          >
            {localizedWeekLabel(parseISO(band.monday), lang)}
          </div>
        ))}
        {window.days.map((day, i) => {
          const date = parseISO(day.date);
          const weekday = localizedWeekday(date, lang);
          return [
            !compact && (
              <div
                key={`w${day.date}`}
                className={`${ganttHeader.dayCell} ${ganttHeader.weekdayCell} ${restClass(day.rest)}`}
                data-today={day.isToday || undefined}
                style={{ gridRow: 3, gridColumn: i + 1 }}
                aria-hidden
              >
                {weekday}
              </div>
            ),
            <div
              key={day.date}
              className={`${ganttHeader.dayCell} ${ganttHeader.dayNumberCell} ${compact ? ganttHeader.compact : ''} ${restClass(day.rest)}`}
              data-rest={day.rest ?? undefined}
              data-today={day.isToday || undefined}
              style={{ gridRow: dayRow, gridColumn: i + 1 }}
              aria-label={`${weekday} ${formatDateRange(day.date, day.date, i18n.language, Number(day.date.slice(0, 4)))}`}
            >
              <span aria-hidden>{localizedDayNumber(date, lang)}</span>
            </div>,
          ];
        })}
      </div>
    </>
  );
}
